"use client";

import { CheckCircle2, Mail, ArrowRight, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/shadcn/button";
import { CopyButton } from "@/components/UI/CopyButton";
import { StepLayout } from "./HowItWorksSteps";

interface DoneStepProps {
  email: string | null;
  isDeploying: boolean;
  onGoToInbox: () => void;
  onClose: () => void;
}

export const DoneStep = ({
  email,
  isDeploying,
  onGoToInbox,
  onClose,
}: DoneStepProps) => {
  if (isDeploying) {
    return (
      <StepLayout showNext={false} showBack={false}>
        <div className="flex flex-col items-center justify-center flex-1 text-center space-y-4 py-16">
          <Loader2 size={32} className="animate-spin text-muted-foreground" />
          <div className="space-y-2">
            <h2 className="text-lg font-medium text-foreground">
              Creating your inbox...
            </h2>
            <p className="text-sm text-muted-foreground max-w-[270px] mx-auto">
              Setting up workflows, knowledge bases, and configurations
            </p>
          </div>
        </div>
      </StepLayout>
    );
  }

  return (
    <StepLayout showNext={false} showBack={false}>
      <div className="flex flex-col items-center justify-center flex-1 text-center space-y-6 py-8">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
        >
          <div className="w-16 h-16 rounded-full bg-success-50 border border-success-100 flex items-center justify-center">
            <CheckCircle2 size={32} className="text-success-600" />
          </div>
        </motion.div>

        <div className="space-y-2">
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="text-xl font-semibold text-foreground"
          >
            Done!
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="text-sm text-muted-foreground"
          >
            Your inbox has been created and is ready to receive emails.
          </motion.p>
        </div>

        {email && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.4 }}
            className="w-full max-w-md"
          >
            <div className="bg-muted/50 border rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2 justify-center text-muted-foreground">
                <Mail size={16} />
                <span className="text-xs font-medium uppercase tracking-wide">
                  Your inbox email
                </span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <code className="text-base font-medium text-foreground">
                  {email}
                </code>
                <CopyButton value={email} iconSize={14} />
              </div>
              <p className="text-xs text-muted-foreground">
                Forward emails to this address to start processing them.
              </p>
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.4 }}
          className="flex items-center gap-3 pt-4"
        >
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={onGoToInbox}>
            Go to Inbox <ArrowRight className="w-4 h-4" />
          </Button>
        </motion.div>
      </div>
    </StepLayout>
  );
};
